import { debounce, get, min, max, round } from 'lodash';
import { IHandleOptions } from './vertical.slider.component';
import * as d3 from 'd3';

export class Handle {
  slider: any;
  scale: any;

  value: number;
  options: IHandleOptions;

  handleLayout: SVGGElement;
  layout: any;

  y = 0;

  constructor(
    slider: SVGGElement,
    scale: any,
    value: number,
    options: IHandleOptions
  ) {
    this.slider = slider;
    this.scale = scale;

    this.value = value;
    this.options = options;

    this.handleLayout = null;
    this.layout = this.createLayout();

    this.y = 0;
  }

  get style(): { top: string } {
    return {
      top: `${this.y + this.options.inputMargin}px`
    };
  }

  get max(): number {
    return this.options.max;
  }

  get min(): number {
    return this.options.min;
  }

  createLayout(): SVGGElement {
    const layout = this.slider
      .append('g')
      .attr('cy', 12)
      .attr('cursor', 'pointer');

    this.handleLayout = this.getHandleLayout(layout);

    layout
      .append('line')
      .attr('x1', this.options.padding.left)
      .attr('y1', 0)
      .attr('x2', this.options.padding.width)
      .attr('y2', 0)
      .attr('stroke', this.options.color)
      .attr('stroke-dasharray', '5, 5');

    if (!this.options.readOnly) {
      layout.call(
        d3
          .drag()
          .on('start.interrupt', () => {
            this.slider.interrupt();
          })
          .on('start drag', this.updateHandle.bind(this))
      );
    }

    return layout;
  }

  getHandleLayout(layout): SVGGElement {
    if (this.options.icon) {
      return layout
        .insert('image', '.track-overlay')
        .attr('class', 'handle')
        .attr('xlink:href', this.options.icon);
    } else {
      return this.createHandleLayout(layout);
    }
  }

  createHandleLayout(layout): SVGGElement {
    const handle = layout.insert('g', '.track-overlay').attr('class', 'handle');

    handle
      .append('circle')
      .attr('r', 10)
      .attr('x', -12)
      .attr('y', -12)
      .attr('fill', '#3371b2');

    handle
      .append('line')
      .attr('y1', 2)
      .attr('y2', 2)
      .attr('x1', -4)
      .attr('x2', 4)
      .attr('stroke-width', 2)
      .attr('stroke', '#fff');

    handle
      .append('line')
      .attr('y1', -2)
      .attr('y2', -2)
      .attr('x1', -4)
      .attr('x2', 4)
      .attr('stroke-width', 2)
      .attr('stroke', '#fff');

    return handle;
  }

  updateHandle(stepVal: number, wait?: boolean): void {
    const step = stepVal || this.options.step;
    const eventY = this.scale.invert(get(d3, 'event.y'));

    const value = eventY || this.value;
    this.value = round(
      this.getNormalizeValue(value, step),
      this.options.precision
    );

    if (step === this.options.step) {
      this.y = this.scale(this.value);
    } else {
      const normalizedValue = this.getNormalizeValue(value, this.options.step);
      this.y = this.scale(normalizedValue);
    }

    this.layout.attr('transform', `translate(0, ${this.y})`);
    if (wait) {
      debounce(() => {
        this.options.callback();
      }, 50);
    } else {
      this.options.callback();
    }
  }

  getNormalizeValue(value: number, step: number = this.options.step): number {
    const val = Math.round(value / step) * step;
    return max([min([val, this.max]), this.min]);
  }
}
